import { useEffect, useMemo, useRef, useState } from 'react';
import { EditorContent, useEditor, type Editor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import { Table } from '@tiptap/extension-table';
import { TableRow } from '@tiptap/extension-table-row';
import { TableCell } from '@tiptap/extension-table-cell';
import { TableHeader } from '@tiptap/extension-table-header';
import Image from '@tiptap/extension-image';
import Placeholder from '@tiptap/extension-placeholder';
import { Extension } from '@tiptap/core';
import { useQuery } from '@tanstack/react-query';
import { api, type Control } from '../../lib/api.ts';
import { Addendum, Appendix, Article, Chapter, Clause, Form, Item, PolicyDoc, Subitem, POLICY_EDITOR_CSS, focusHead } from '../../lib/policy/schema.ts';

const STRUCT = ['chapter', 'article', 'clause', 'item', 'subitem', 'addendum', 'appendix', 'form'];
const CHILD: Record<string, string | null> = { article: 'clause', clause: 'item', item: 'subitem', subitem: null };
const PARENT: Record<string, string | null> = { clause: 'article', item: 'clause', subitem: 'item' };

/** 키보드: Tab 들여쓰기(항→호→목), Shift+Tab 내어쓰기, Mod+Enter 같은 종류 노드 추가 */
const PolicyKeys = Extension.create({
  name: 'policyKeys',
  addKeyboardShortcuts() {
    return {
      Tab: () => indent(this.editor),
      'Shift-Tab': () => outdent(this.editor),
      'Mod-Enter': () => addSibling(this.editor),
    };
  },
});

function structAt(editor: Editor) {
  const { $from } = editor.state.selection;
  for (let d = $from.depth; d > 0; d--) { const n = $from.node(d); if (STRUCT.includes(n.type.name)) return { node: n, depth: d, pos: $from.before(d) }; }
  return null;
}
/** 현재 문단을 하위 구조로 감싼다 (조 안 문단 → 항, 항 안 → 호, 호 안 → 목) */
function indent(editor: Editor) {
  const s = structAt(editor); if (!s) return false;
  const child = CHILD[s.node.type.name]; if (!child) return false;
  const { $from } = editor.state.selection;
  const pDepth = $from.depth; const para = $from.node(pDepth); if (para.type.name !== 'paragraph') return false;
  const from = $from.before(pDepth), to = $from.after(pDepth);
  const type = editor.schema.nodes[child];
  const wrapped = type.create(null, para);
  return editor.chain().focus().command(({ tr }) => { tr.replaceWith(from, to, wrapped); return true; }).run();
}
/** 현재 구조 노드를 풀어 상위 레벨 문단으로 */
function outdent(editor: Editor) {
  const s = structAt(editor); if (!s || !PARENT[s.node.type.name]) return false;
  const from = s.pos, to = s.pos + s.node.nodeSize;
  return editor.chain().focus().command(({ tr }) => { tr.replaceWith(from, to, s.node.content); return true; }).run();
}
/** 같은 종류의 구조 노드를 바로 뒤에 추가 */
function addSibling(editor: Editor) {
  const s = structAt(editor); if (!s) return false;
  const type = s.node.type;
  const attrs = type.name === 'article' ? { title: '' } : type.name === 'chapter' ? { title: '' } : null;
  const content = type.name === 'chapter' ? [editor.schema.nodes.article.create({ title: '' }, editor.schema.nodes.paragraph.create())] : [editor.schema.nodes.paragraph.create()];
  const node = type.create(attrs, content);
  const pos = s.pos + s.node.nodeSize;
  const ok = editor.chain().focus().command(({ tr }) => { tr.insert(pos, node); return true; }).setTextSelection(pos + 2).run();
  if (ok && (type.name === 'chapter' || type.name === 'article')) focusHead(editor, pos);
  return ok;
}
/** 본문(장·조) 끝 = 첫 부칙·별표·별지 앞. 장·조는 부칙 뒤로 가지 않는다 */
function bodyEnd(editor: Editor) {
  const { doc } = editor.state;
  let pos = 0, end = doc.content.size;
  doc.forEach((n) => { if (end === doc.content.size && ['addendum', 'appendix', 'form'].includes(n.type.name)) end = pos; pos += n.nodeSize; });
  return end;
}
/** 커서가 속한 최상위 노드 (장이면 장, 장 밖 조면 조) */
function topAt(editor: Editor) {
  const { $from } = editor.state.selection;
  return $from.depth > 0 ? { node: $from.node(1), pos: $from.before(1) } : null;
}
function insertTop(editor: Editor, kind: 'chapter' | 'article' | 'addendum' | 'appendix' | 'form') {
  const { schema, doc, selection } = editor.state;
  const para = schema.nodes.paragraph.create();
  if (kind === 'chapter') {
    // 장 앞에 떠 있는 조(맨 앞 연속 조)가 있으면 새 장으로 감싼다 → 제1장이 제1조보다 앞에 온다
    let lead = 0;
    for (let i = 0; i < doc.childCount && doc.child(i).type.name === 'article'; i++) lead += doc.child(i).nodeSize;
    if (lead > 0) {
      const chapter = schema.nodes.chapter.create({ title: '' }, doc.content.cut(0, lead));
      editor.chain().focus().command(({ tr }) => { tr.replaceWith(0, lead, chapter); return true; }).run();
      focusHead(editor, 0);
      return;
    }
    const top = topAt(editor);
    const pos = top?.node.type.name === 'chapter' ? top.pos + top.node.nodeSize : bodyEnd(editor);
    const node = schema.nodes.chapter.create({ title: '' }, [schema.nodes.article.create({ title: '' }, para)]);
    editor.chain().focus().command(({ tr }) => { tr.insert(pos, node); return true; }).setTextSelection(pos + 3).run();
    focusHead(editor, pos);
    return;
  }
  if (kind === 'article') {
    const node = schema.nodes.article.create({ title: '' }, para);
    // 커서가 조 안이면 그 조 바로 뒤(같은 장), 아니면 마지막 장 끝 또는 본문 끝
    const { $from } = selection;
    let pos: number | null = null;
    for (let d = $from.depth; d > 0; d--) if ($from.node(d).type.name === 'article') { pos = $from.after(d); break; }
    if (pos == null) {
      const end = bodyEnd(editor);
      const $end = doc.resolve(end);
      const last = $end.nodeBefore;
      pos = last?.type.name === 'chapter' ? end - 1 : end;
    }
    editor.chain().focus().command(({ tr }) => { tr.insert(pos, node); return true; }).setTextSelection(pos + 2).run();
    focusHead(editor, pos);
    return;
  }
  const node = schema.nodes[kind].create(kind === 'addendum' ? { title: '' } : { title: '', no: '' }, para);
  const end = doc.content.size;
  editor.chain().focus().command(({ tr }) => { tr.insert(end, node); return true; }).run();
  focusHead(editor, end);
}

export interface PolicyEditorProps { initial: unknown; onChange: (json: unknown) => void; editable?: boolean; onActiveArticle?: (attrs: Record<string, unknown> | null) => void }

/** 규정 구조 편집기 (TipTap 3) */
export function PolicyEditor({ initial, onChange, editable = true, onActiveArticle }: PolicyEditorProps) {
  const controls = useQuery({ queryKey: ['controls', '', ''], queryFn: () => api<Control[]>('/controls') });
  const [active, setActive] = useState<{ pos: number; no: number; attrs: Record<string, unknown> } | null>(null);
  const changeRef = useRef(onChange); changeRef.current = onChange;
  const activeRef = useRef(onActiveArticle); activeRef.current = onActiveArticle;
  /** 커서가 속한 조 (제목은 머리에서 고치므로 문서가 바뀔 때도 다시 읽는다) */
  const syncActive = (editor: Editor) => {
    const { $from } = editor.state.selection;
    for (let d = $from.depth; d > 0; d--) {
      const n = $from.node(d);
      if (n.type.name !== 'article') continue;
      const pos = $from.before(d);
      let no = 0;
      editor.state.doc.nodesBetween(0, pos + 1, (c, p) => { if (c.type.name === 'article' && p <= pos) no += 1; return c.type.name === 'doc' || c.type.name === 'chapter'; });
      setActive({ pos, no, attrs: n.attrs }); activeRef.current?.(n.attrs);
      return;
    }
    setActive(null); activeRef.current?.(null);
  };
  const editor = useEditor({
    extensions: [StarterKit.configure({ document: false, heading: false, codeBlock: false, blockquote: false, horizontalRule: false }), PolicyDoc, Chapter, Article, Clause, Item, Subitem, Addendum, Appendix, Form, Table.configure({ resizable: false }), TableRow, TableCell, TableHeader, Image, Placeholder.configure({ placeholder: '내용을 입력하세요' }), PolicyKeys],
    content: initial as never,
    editable,
    onUpdate: ({ editor }) => { changeRef.current(editor.getJSON()); syncActive(editor); },
    onSelectionUpdate: ({ editor }) => syncActive(editor),
  });
  useEffect(() => { editor?.setEditable(editable); }, [editable, editor]);
  const css = useMemo(() => POLICY_EDITOR_CSS, []);
  if (!editor) return null;
  const setArticleAttr = (patch: Record<string, unknown>) => { if (!active) return; editor.chain().focus().command(({ tr }) => { const n = tr.doc.nodeAt(active.pos); if (!n) return false; tr.setNodeMarkup(active.pos, undefined, { ...n.attrs, ...patch }); return true; }).run(); setActive({ ...active, attrs: { ...active.attrs, ...patch } }); };
  const tb = ' border border-slate-300 px-2 py-1 text-xs hover:bg-slate-50 disabled:opacity-40';
  return (
    <div className="pn-editor grid gap-3 md:grid-cols-[1fr_260px]">
      <style>{css}</style>
      <div>
        {editable && (
          <div className="flex flex-wrap gap-1 mb-2 sticky top-0 bg-white py-1 z-10 border-b border-slate-200">
            <button className={tb} onClick={() => insertTop(editor, 'article')}>+ 조</button>
            <button className={tb} onClick={() => insertTop(editor, 'chapter')}>+ 장</button>
            <button className={tb} onClick={() => indent(editor)} title="Tab">항/호/목 만들기</button>
            <button className={tb} onClick={() => outdent(editor)} title="Shift+Tab">풀기</button>
            <button className={tb} onClick={() => addSibling(editor)} title="⌘/Ctrl+Enter">같은 단위 추가</button>
            <span className="w-px bg-slate-200 mx-1" />
            <button className={tb} onClick={() => editor.chain().focus().toggleBold().run()}><b>B</b></button>
            <button className={tb} onClick={() => editor.chain().focus().toggleItalic().run()}><i>I</i></button>
            <button className={tb} onClick={() => editor.chain().focus().insertTable({ rows: 2, cols: 3, withHeaderRow: true }).run()}>표</button>
            <button className={tb} disabled={!editor.can().addRowAfter()} onClick={() => editor.chain().focus().addRowAfter().run()}>행+</button>
            <button className={tb} disabled={!editor.can().addColumnAfter()} onClick={() => editor.chain().focus().addColumnAfter().run()}>열+</button>
            <button className={tb} disabled={!editor.can().deleteTable()} onClick={() => editor.chain().focus().deleteTable().run()}>표 삭제</button>
            <span className="w-px bg-slate-200 mx-1" />
            <button className={tb} onClick={() => insertTop(editor, 'addendum')}>+ 부칙</button>
            <button className={tb} onClick={() => insertTop(editor, 'appendix')}>+ 별표</button>
            <button className={tb} onClick={() => insertTop(editor, 'form')}>+ 별지</button>
            <span className="flex-1" />
            <button className={tb} onClick={() => editor.chain().focus().undo().run()}>↶</button>
            <button className={tb} onClick={() => editor.chain().focus().redo().run()}>↷</button>
          </div>
        )}
        <div className="bg-white border border-slate-200 p-3 min-h-96 text-sm leading-[1.5]"><EditorContent editor={editor} /></div>
        <div className="text-xs text-slate-500 mt-1">제목은 제n장·제n조 옆 칸을 눌러 바로 입력(Enter: 본문으로) · Tab: 문단을 항→호→목으로 · Shift+Tab: 풀기 · ⌘/Ctrl+Enter: 같은 단위 추가. 번호는 자동으로 매겨집니다.</div>
      </div>
      <aside className="text-sm">
        <div className="bg-white border border-slate-200 p-3 sticky top-0">
          <div className="font-semibold mb-2">{active ? `제${active.no}조${active.attrs.title ? `(${String(active.attrs.title)})` : ''}` : '조를 선택하세요'}</div>
          {active && (
            <div className="space-y-2">
              <label className="block text-xs text-slate-600">통제항목 매핑
                <select multiple className="mt-1 w-full border border-slate-300 px-2 py-1 h-40 text-xs" disabled={!editable} value={(active.attrs.controlIds as string[]) ?? []} onChange={(e) => setArticleAttr({ controlIds: [...e.target.selectedOptions].map((o) => o.value) })}>
                  {controls.data?.map((c) => <option key={c.id} value={c.id}>{c.code} {c.title}</option>)}
                </select>
              </label>
              <div className="text-xs text-slate-500">매핑된 통제항목 {((active.attrs.controlIds as string[]) ?? []).length}개</div>
            </div>
          )}
        </div>
      </aside>
    </div>
  );
}
